import { Field, Form } from "react-final-form";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import arrayMutators from "final-form-arrays";
import classNames from "classnames";
import EntityDialog from "rs-components/EntityDialog";
import InputsToOptimize from "./components/InputsToOptimize";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import ParametersToOptimize, { checkSomeParameterNotSaved } from "./components/ParametersToOptimize";
import OptimizationPreview from "./components/OptimizationPreview";
import { isNotEmpty } from "utils/validate";
import { isOptimizationCompleted } from "store/rs-workspace/utils/completeness.utils";
import { addOptimization, cancelOutdatedPreview, editOptimization, getOptimizationDefaultName, initOptimizationPreview, resetOptimizationDefaultName, setCurrentOptimization, setOptimizationDialogOpen, } from "store/rs-workspace/actions/rs-workspace.actions";
import { optimizationDefaultNameSelector, optimizationDialogInitialValuesSelector, persistedInsightsDataSelector, temporaryOptimizationPreviewSelector, incompleteSimulationsIdsSelector, workspaceCurrentOptimizationSelector, workspaceInsightsSelector, workspaceSimulationsSelector, workspaceRecipesSelector, workspaceCellModelsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { GetRestrictionsMethod, RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { isEditableSelector, restrictionsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { getRestrictions } from "store/knowledge-base-item/knowledge-base-item.actions";
import { CancelOutdatedPreviewType, OptimizationParameterParameterType, OptimizationTargetType, } from "store/rs-workspace/rs-workspace.types";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { checkOptimizationErrored } from "store/rs-workspace/utils/insights.utils";
import { processVariablesSelector } from "store/global/global.selectors";
import { INCORRECT_INPUTS_MESSAGE } from "shared/labels";
import { RsWorkspaceEntityType } from "shared/types";
import { RecipeScalingApiEndpoints } from "api/api-endpoints";
import * as styles from "./styles.module.scss";
var RightSideViewMode;
(function (RightSideViewMode) {
    RightSideViewMode["parameters"] = "parameters";
    RightSideViewMode["preview"] = "preview";
})(RightSideViewMode || (RightSideViewMode = {}));
export var FormFields;
(function (FormFields) {
    FormFields["name"] = "name";
    FormFields["state_input"] = "state.input";
    FormFields["state_parameters"] = "state.parameters";
})(FormFields || (FormFields = {}));
export const DEFAULT_TARGET = OptimizationTargetType.RECIPE;
const getRestrictionsPayload = (post) => ({
    rsEndpoint: RecipeScalingApiEndpoints.workspaces,
    template: post ? RequestTemplate.OPTIMIZATION_POST : RequestTemplate.OPTIMIZATION_PUT,
    method: post ? GetRestrictionsMethod.POST : GetRestrictionsMethod.PUT,
});
const OptimizationDialog = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [rightSideViewMode, setRightSideViewMode] = useState(RightSideViewMode.parameters);
    const optimizationDefaultName = useSelector(optimizationDefaultNameSelector);
    const currentOptimization = useSelector(workspaceCurrentOptimizationSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const restrictions = useSelector(restrictionsSelector(getRestrictionsPayload(!currentOptimization)));
    const initialValues = useSelector(optimizationDialogInitialValuesSelector);
    const temporaryOptimizationPreview = useSelector(temporaryOptimizationPreviewSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const processVariables = useSelector(processVariablesSelector);
    const incompleteSimulationIds = useSelector(incompleteSimulationsIdsSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const editable = useSelector(isEditableSelector);
    const getRightSideViewModeInputOptions = useCallback((showIncorrectInputsMessage) => [
        { value: RightSideViewMode.parameters, label: "Parameters to optimize" },
        {
            value: RightSideViewMode.preview,
            label: "Preview",
            title: showIncorrectInputsMessage ? INCORRECT_INPUTS_MESSAGE : undefined,
        },
    ], []);
    const optimizationErrored = useMemo(() => {
        const { insightId, insightInputSetId, outputId } = { ...currentOptimization?.state?.input };
        return checkOptimizationErrored(insightId, insightInputSetId, outputId, persistedInsightsData);
    }, [currentOptimization?.state?.input, persistedInsightsData]);
    const onUpsertOptimization = useCallback((values) => {
        const payload = {
            ...values,
            name: values[FormFields.name] ?? optimizationDefaultName,
            state: {
                ...values?.state,
                parameters: values?.state?.parameters
                    ?.map((parametersSection) => ({
                    ...parametersSection,
                    parameters: parametersSection?.parameters?.filter((parameter) => parameter.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE
                        ? isNotEmpty(parameter.parameterId)
                        : isNotEmpty(parameter.stepId)),
                }))
                    .filter(({ inputId }) => isNotEmpty(inputId)),
            },
            workspaceId: parseInt(id),
        };
        if (currentOptimization) {
            dispatch(editOptimization({
                ...payload,
                optimizationId: currentOptimization.id,
            }));
        }
        else {
            dispatch(addOptimization(payload));
        }
    }, [currentOptimization, dispatch, id, optimizationDefaultName]);
    const resetPreview = useCallback(() => editable &&
        dispatch(cancelOutdatedPreview({
            previewType: CancelOutdatedPreviewType.Optimization,
            elementId: currentOptimization?.id ?? -1,
        })), [currentOptimization?.id, dispatch, editable]);
    const onDialogClosed = useCallback((form) => {
        dispatch(resetOptimizationDefaultName());
        dispatch(setCurrentOptimization(null));
        form.reset();
        setRightSideViewMode(RightSideViewMode.parameters);
    }, [dispatch]);
    const onDialogClose = useCallback((form) => {
        dispatch(setOptimizationDialogOpen(false));
        onDialogClosed(form);
    }, [dispatch, onDialogClosed]);
    const onDialogOpening = useCallback(() => {
        dispatch(initOptimizationPreview(currentOptimization?.id ?? -1));
        if (!currentOptimization) {
            dispatch(getOptimizationDefaultName(parseInt(id)));
        }
    }, [dispatch, id, currentOptimization]);
    useEffect(() => {
        !restrictions && dispatch(getRestrictions(getRestrictionsPayload(!currentOptimization)));
    }, [restrictions, dispatch, currentOptimization]);
    return (React.createElement(Form, { onSubmit: onUpsertOptimization, initialValues: initialValues, mutators: { ...arrayMutators }, validateOnBlur: true, render: ({ handleSubmit, form, values, ...renderProps }) => {
            const isCompleted = isOptimizationCompleted(values?.state, insights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes);
            const errored = optimizationErrored ||
                (isCompleted && isNotEmpty(temporaryOptimizationPreview?.result?.errorMessage, true));
            const someParametersNotSaved = checkSomeParameterNotSaved(values?.state?.parameters);
            return (React.createElement(EntityDialog, { nameFieldName: FormFields.name, nameFieldRestrictions: {
                    ...getFieldRestrictions(restrictions, [getRequestName(FormFields.name)]),
                    Default: "Optimization 1",
                }, entityType: RsWorkspaceEntityType.Optimization, isOpen: true, onDialogClose: () => onDialogClose(form), onCancel: resetPreview, onDialogOpening: onDialogOpening, entityCompleted: isCompleted, entityErrored: errored, handleSubmit: handleSubmit, formRenderProps: renderProps, selectedId: currentOptimization?.id ?? -1 },
                React.createElement(Field, { name: FormFields.state_parameters }, () => null),
                React.createElement("div", { className: styles.root },
                    React.createElement(InputsToOptimize, { restrictions: restrictions, currentOptimization: currentOptimization }),
                    React.createElement("div", { className: styles.rightSide },
                        React.createElement(RadioInputGroup, { onChange: (value) => setRightSideViewMode(value), value: rightSideViewMode, items: getRightSideViewModeInputOptions(someParametersNotSaved), isPageSection: true, className: styles.radioInput, readonly: someParametersNotSaved }),
                        React.createElement("div", { className: classNames(styles.rightSideContent, {
                                [styles.visible]: rightSideViewMode === RightSideViewMode.parameters,
                            }) },
                            React.createElement(ParametersToOptimize, { restrictions: restrictions })),
                        React.createElement("div", { className: classNames(styles.rightSideContent, {
                                [styles.visible]: rightSideViewMode === RightSideViewMode.preview,
                            }) },
                            React.createElement(OptimizationPreview, { optimizationState: values.state, optimizationId: values.id, isCompleted: isCompleted, isVisible: rightSideViewMode === RightSideViewMode.preview }))))));
        } }));
};
export default OptimizationDialog;
